import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Shield, Eye, EyeOff, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAdminAuth();
  const { t } = useTranslation();
  const { lang, toggleLanguage, dir } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin');
    } catch {
      toast.error(t('admin.login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center p-4" dir={dir}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
      </div>

      {/* Language Toggle */}
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleLanguage}
        data-testid="admin-login-lang-toggle"
        className="absolute top-4 end-4 text-slate-400 hover:text-white gap-1.5 z-10"
      >
        <Globe className="w-4 h-4" />
        <span className="text-xs">{lang === 'ar' ? 'EN' : 'عربي'}</span>
      </Button>

      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/20">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">{t('admin.login.title')}</h1>
          <p className="text-sm text-slate-500">{t('admin.login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-login-form">
          <div className="bg-[#0d1224] border border-slate-800/60 rounded-xl p-6 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1.5 block">{t('admin.login.email')}</label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@zouqai.com"
                data-testid="admin-email-input"
                className="bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-600 focus:border-red-500/50 focus:ring-red-500/20"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1.5 block">{t('admin.login.password')}</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                  className="bg-slate-900/50 border-slate-700/50 text-white placeholder:text-slate-600 focus:border-red-500/50 focus:ring-red-500/20 pe-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white font-medium h-11 rounded-xl shadow-lg shadow-red-500/20"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : t('admin.login.signIn')}
          </Button>
        </form>

        <p className="text-center text-xs text-slate-600 mt-6">
          {t('admin.login.restricted')}
        </p>
      </div>
    </div>
  );
}
